import {
  useId,
  useRef,
  useLayoutEffect,
  type ChangeEvent,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import { ComboClearButton } from '@/components/ui/ComboClearButton/ComboClearButton';
import {
  caretIndexAfterDigitCount,
  caretDigitTargetAfterEdit,
  extractRuPhoneDigits,
  formatRuPhoneDisplay,
} from '@/utils/phoneRu';
import '@/components/ui/LeadingIconField/leadingIconField.css';

export interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
  id?: string;
  /** Подсказка под полем (например формат) */
  hint?: string;
}

export function PhoneInput({
  value,
  onChange,
  disabled,
  fullWidth,
  placeholder = 'Телефон',
  id,
  hint,
}: PhoneInputProps) {
  const reactId = useId().replace(/:/g, '');
  const inputId = id ?? `phone-input-${reactId}`;
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingDigitsBeforeCaret = useRef<number | null>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const el = e.target;
    const extracted = extractRuPhoneDigits(el.value);
    const formatted = formatRuPhoneDisplay(extracted);
    pendingDigitsBeforeCaret.current = caretDigitTargetAfterEdit(
      el.value,
      el.selectionStart,
      extracted
    );
    onChange(formatted);
  };

  useLayoutEffect(() => {
    if (pendingDigitsBeforeCaret.current == null || !inputRef.current) return;
    const d = pendingDigitsBeforeCaret.current;
    pendingDigitsBeforeCaret.current = null;
    const pos = caretIndexAfterDigitCount(value, d);
    inputRef.current.setSelectionRange(pos, pos);
  }, [value]);

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text/plain') ?? '';
    const extracted = extractRuPhoneDigits(pasted);
    const formatted = formatRuPhoneDisplay(extracted);
    onChange(formatted);
    pendingDigitsBeforeCaret.current = Math.min(11, extracted.replace(/\D/g, '').length);
  };

  const clear = () => {
    onChange('');
    inputRef.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape' && value) {
      e.preventDefault();
      clear();
    }
  };

  const hasClear = Boolean(value) && !disabled;

  return (
    <div
      className={`form__field ${fullWidth ? 'form__field_full' : ''} leading-icon-field ${fullWidth ? 'leading-icon-field_full-width' : ''}`}
    >
      <div
        className={`leading-icon-field__shell ${hasClear ? 'leading-icon-field__shell_has-clear' : ''}`}
      >
        <span className="leading-icon-field__icon" aria-hidden>
          <PhoneOutlined sx={{ fontSize: 18, color: 'var(--text-muted)', opacity: 0.9 }} />
        </span>
        <input
          ref={inputRef}
          id={inputId}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          spellCheck={false}
          enterKeyHint="done"
          className="leading-icon-field__input leading-icon-field__input_tabular"
          value={value}
          onChange={handleChange}
          onPaste={handlePaste}
          onKeyDown={onKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          aria-describedby={hint ? `${inputId}-hint` : undefined}
        />
        {hasClear ? <ComboClearButton onClick={clear} className="leading-icon-field__clear" /> : null}
      </div>
      {hint ? (
        <span id={`${inputId}-hint`} className="leading-icon-field__hint">
          {hint}
        </span>
      ) : null}
    </div>
  );
}
